import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Users, Camera, X, AlertCircle, Clock, CheckCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Polling interval for realtime chat (ms)
const POLL_INTERVAL = 5000;

const AgronomistCaseButton = ({ fieldId, fieldName }) => {
  const { getAuthHeaders } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [showCases, setShowCases] = useState(false);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseMessages, setCaseMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [myAgronomist, setMyAgronomist] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    urgency: 'day',
    photo_urls: []
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Check if farmer has an assigned agronomist
  useEffect(() => {
    const checkMyAgronomist = async () => {
      try {
        const response = await axios.get(`${API}/my-agronomist`, {
          headers: getAuthHeaders()
        });
        setMyAgronomist(response.data);
      } catch (error) {
        console.error('Error checking agronomist:', error);
      }
    };
    checkMyAgronomist();
  }, [getAuthHeaders]);

  // Fetch cases for this field
  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases?field_id=${fieldId}`, {
        headers: getAuthHeaders()
      });
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({
        ...prev,
        photo_urls: [...prev.photo_urls, response.data.url]
      }));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Remove photo
  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  // Create case
  const handleCreateCase = async () => {
    if (!formData.description || formData.description.trim().length < 10) {
      toast.error('Please provide a description (min 10 characters)');
      return;
    }
    if (formData.photo_urls.length === 0) {
      toast.error('At least one photo is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/cases`, {
        field_id: fieldId,
        description: formData.description,
        urgency: formData.urgency,
        photo_urls: formData.photo_urls
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Case created! An agronomist will review it soon.');
      setShowModal(false);
      setFormData({ description: '', urgency: 'day', photo_urls: [] });
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  // Load messages for a case
  const loadCaseMessages = useCallback(async (caseId) => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/messages`, {
        headers: getAuthHeaders()
      });
      setCaseMessages(response.data);
      // Scroll to bottom on new messages
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [getAuthHeaders]);

  // Start polling when a case is selected and chat is open
  useEffect(() => {
    if (selectedCase && selectedCase.status === 'assigned') {
      // Initial load
      loadCaseMessages(selectedCase.id);
      
      // Start polling
      pollIntervalRef.current = setInterval(() => {
        loadCaseMessages(selectedCase.id);
      }, POLL_INTERVAL);
      
      // Stop polling on tab hidden
      const handleVisibilityChange = () => {
        if (document.hidden && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        } else if (!document.hidden && selectedCase) {
          loadCaseMessages(selectedCase.id);
          pollIntervalRef.current = setInterval(() => {
            loadCaseMessages(selectedCase.id);
          }, POLL_INTERVAL);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [selectedCase, loadCaseMessages]);

  // Stop polling when modal is closed
  useEffect(() => {
    if (!showCases && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [showCases]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedCase) return;

    try {
      await axios.post(`${API}/cases/${selectedCase.id}/messages`, {
        text: newMessage,
        image_urls: []
      }, {
        headers: getAuthHeaders()
      });
      setNewMessage('');
      loadCaseMessages(selectedCase.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const openCases = cases.filter(c => c.status !== 'resolved');
  const resolvedCases = cases.filter(c => c.status === 'resolved');

  return (
    <>
      {/* Main Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            background: 'var(--success)',
            color: 'white'
          }}
          data-testid="agronomist-case-btn"
        >
          <Users className="w-4 h-4" />
          {myAgronomist?.has_assigned_agronomist 
            ? `Ask ${myAgronomist.agronomist.name}` 
            : 'New Case'}
        </button>
        <button
          onClick={() => { setShowCases(true); fetchCases(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            background: 'transparent',
            border: '1px solid var(--success)',
            color: 'var(--success)'
          }}
          data-testid="view-cases-btn"
        >
          <MessageSquare className="w-4 h-4" />
          Agronomist Cases
        </button>
      </div>

      {/* Create Case Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="card-floating max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Create Agronomist Case</h3>
              <button onClick={() => setShowModal(false)} style={{ padding: 8, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Show assigned agronomist info */}
            {myAgronomist?.has_assigned_agronomist && (
              <div style={{ padding: 12, marginBottom: 16, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10 }}>
                <p style={{ fontSize: 13, color: 'var(--success)' }}>
                  <strong>Your questions will go directly to:</strong> {myAgronomist.agronomist.name}
                </p>
              </div>
            )}
            
            {!myAgronomist?.has_assigned_agronomist && (
              <div style={{ padding: 12, marginBottom: 16, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10 }}>
                <p style={{ fontSize: 13, color: 'var(--info)' }}>
                  Your case will be visible to all available agronomists.
                </p>
              </div>
            )}

            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Field: <strong style={{ color: 'var(--text)' }}>{fieldName}</strong>
            </p>

            <div className="space-y-4">
              {/* Photo Upload */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                  Photos <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.photo_urls.map((url, i) => (
                    <div key={i} className="relative">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                        alt=""
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(i)}
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          width: 20,
                          height: 20,
                          background: 'var(--danger)',
                          color: 'white',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    width: '100%',
                    padding: 24,
                    border: '2px dashed var(--border)',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <Camera className="w-6 h-6" style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {uploading ? 'Uploading...' : 'Add Photo'}
                  </span>
                </button>
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
                  Description <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the issue you're observing..."
                  rows={4}
                  className="input-depth"
                  style={{ width: '100%', resize: 'none' }}
                />
              </div>

              {/* Urgency */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Urgency</label>
                <select
                  value={formData.urgency}
                  onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                  className="input-depth"
                  style={{ width: '100%' }}
                >
                  <option value="immediate">Immediate</option>
                  <option value="day">Within the day</option>
                  <option value="48h">Next 48 hours</option>
                </select>
              </div>

              <button
                onClick={handleCreateCase}
                disabled={loading || formData.photo_urls.length === 0}
                className="btn-depth-primary"
                style={{ width: '100%', padding: 12, opacity: loading || formData.photo_urls.length === 0 ? 0.5 : 1 }}
              >
                {loading ? 'Creating...' : 'Submit Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Cases Modal */}
      {showCases && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
          <div className="card-floating max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text)' }}>Agronomist Cases</h3>
              <button onClick={() => { setShowCases(false); setSelectedCase(null); }} className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <X className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {!selectedCase ? (
                <div className="space-y-4">
                  {/* Active Cases */}
                  {openCases.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Active Cases</h4>
                      <div className="space-y-2">
                        {openCases.map(c => (
                          <div
                            key={c.id}
                            onClick={() => { setSelectedCase(c); loadCaseMessages(c.id); }}
                            className="p-3 rounded-lg cursor-pointer transition"
                            style={{
                              background: c.status === 'assigned' ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${c.status === 'assigned' ? 'rgba(34,197,94,0.3)' : 'var(--border-soft)'}`
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                {c.status === 'open' ? (
                                  <Clock className="w-4 h-4" style={{ color: 'var(--warning)' }} />
                                ) : (
                                  <MessageSquare className="w-4 h-4" style={{ color: 'var(--success)' }} />
                                )}
                                <span className="text-xs px-2 py-0.5 rounded-full" style={{
                                  background: c.urgency === 'immediate' ? 'rgba(239,68,68,0.15)' :
                                              c.urgency === 'day' ? 'rgba(245,158,11,0.15)' : 'rgba(59,130,246,0.15)',
                                  color: c.urgency === 'immediate' ? 'var(--danger)' :
                                         c.urgency === 'day' ? 'var(--warning)' : 'var(--info)'
                                }}>
                                  {c.urgency}
                                </span>
                              </div>
                              <span className="text-xs" style={{ color: c.status === 'assigned' ? 'var(--success)' : 'var(--text-muted)' }}>
                                {c.status === 'assigned' ? `With ${c.assigned_agronomist_name}` : 'Waiting...'}
                              </span>
                            </div>
                            <p className="text-sm mt-2 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{c.description}</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                              {new Date(c.created_at_utc).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Resolved Cases */}
                  {resolvedCases.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Resolved</h4>
                      <div className="space-y-2">
                        {resolvedCases.map(c => (
                          <div
                            key={c.id}
                            onClick={() => { setSelectedCase(c); loadCaseMessages(c.id); }}
                            className="p-3 rounded-lg cursor-pointer transition"
                            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-soft)' }}
                          >
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{c.description.substring(0, 50)}...</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {cases.length === 0 && (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                      <p style={{ color: 'var(--text-muted)' }}>No cases yet</p>
                      <button
                        onClick={() => { setShowCases(false); setShowModal(true); }}
                        className="mt-4"
                        style={{ color: 'var(--success)', background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        Create your first case
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Case Detail View */
                <div className="space-y-4">
                  <button
                    onClick={() => setSelectedCase(null)}
                    className="text-sm"
                    style={{ color: 'var(--success)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    ← Back to cases
                  </button>

                  <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-soft)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{
                        background: selectedCase.status === 'resolved' ? 'rgba(255,255,255,0.1)' :
                                    selectedCase.status === 'assigned' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                        color: selectedCase.status === 'resolved' ? 'var(--text-muted)' :
                               selectedCase.status === 'assigned' ? 'var(--success)' : 'var(--warning)'
                      }}>
                        {selectedCase.status}
                      </span>
                      {selectedCase.assigned_agronomist_name && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          Agronomist: {selectedCase.assigned_agronomist_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text)' }}>{selectedCase.description}</p>
                    {selectedCase.photo_urls?.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {selectedCase.photo_urls.map((url, i) => (
                          <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover rounded" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Chat */}
                  {selectedCase.status === 'assigned' && (
                    <div className="rounded-lg" style={{ border: '1px solid var(--border-soft)' }}>
                      <div className="p-3 text-sm font-medium flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--text)' }}>
                        <span>Chat</span>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Auto-refreshing</span>
                      </div>
                      <div className="h-48 overflow-y-auto p-3 space-y-2" style={{ scrollBehavior: 'smooth' }}>
                        {caseMessages.map(msg => (
                          <div
                            key={msg.id}
                            className="p-2 rounded-lg max-w-[80%]"
                            style={{
                              marginLeft: msg.sender_role === 'farmer' ? 'auto' : 0,
                              background: msg.sender_role === 'farmer' ? 'rgba(59,130,246,0.15)' : 'rgba(34,197,94,0.15)',
                              border: `1px solid ${msg.sender_role === 'farmer' ? 'rgba(59,130,246,0.3)' : 'rgba(34,197,94,0.3)'}`
                            }}
                          >
                            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{msg.sender_name}</p>
                            <p className="text-sm" style={{ color: 'var(--text)' }}>{msg.text}</p>
                            {msg.image_urls?.length > 0 && (
                              <div className="flex gap-1 mt-2 flex-wrap">
                                {msg.image_urls.map((url, i) => (
                                  <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-12 h-12 object-cover rounded" />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {caseMessages.length === 0 && (
                          <p className="text-center text-sm" style={{ color: 'var(--text-muted)' }}>No messages yet</p>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                      <div className="p-3 flex gap-2" style={{ borderTop: '1px solid var(--border-soft)' }}>
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="input-depth flex-1 text-sm"
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        />
                        <button
                          onClick={handleSendMessage}
                          className="btn-depth-primary text-sm"
                        >
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgronomistCaseButton;
